import {
  Box,
  Button,
  Heading,
  Image,
  VStack,
  HStack,
  Input,
  FormControl,
  FormLabel,
  IconButton,
  Tooltip,
  Flex,
  useToast,
  SimpleGrid,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Code,
} from "@chakra-ui/react";

import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import CodesetSelect from "./CodesetSelect";
import UuidPreview from "./UuidPreview";
import {
  HiOutlineArrowNarrowLeft,
  HiOutlinePlus,
  HiOutlineSave,
  HiX,
  HiOutlineTrash,
} from "react-icons/hi";
import Navbar from "./Navbar";
import HeroImage from "./images/stamps.png";
import PartySelectById from "./PartySelectById";
import { debounce } from "lodash";
import { useAuth } from "./AuthContext";
import { canEditRecord, withOwnerOrganisation } from "./utils/ownership";
import { errorFromResponse } from "./utils/apiErrors";

const JournalsEditor = ({ journal, onClose, isNew }) => {
  const [journalHeader, setJournalHeader] = useState({});
  const [journalLines, setJournalLines] = useState([]);
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const isReadOnly = !isNew && !canEditRecord(user, journal);

  useEffect(() => {
    if (journal) {
      setJournalHeader({ ...journal, journalLines: undefined });
      setJournalLines(journal.journalLines || []);
    }
  }, [journal]);

  const handleHeaderChange = useMemo(
    () =>
      debounce((field, value) => {
        setJournalHeader((prev) => {
          if (prev[field] === value) return prev;
          return { ...prev, [field]: value };
        });
      }, 25),
    []
  );

  const handleSave = async () => {
    try {
      const fullJournal = { ...journalHeader, journalLines };
      const response = await fetch(
        isNew
          ? "https://ledgers.poc.mrc.ai/api/journals"
          : `https://ledgers.poc.mrc.ai/api/journals/${journalHeader._id}`,
        {
          method: isNew ? "POST" : "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(isNew ? withOwnerOrganisation(fullJournal, user) : fullJournal),
        }
      );

      if (!response.ok) {
        throw await errorFromResponse(
          response,
          `Failed to ${isNew ? "create" : "save"}. Status: ${response.status}`
        );
      }

      const result = await response.json();

      toast({
        title: isNew ? "Journal created" : "Journal saved",
        description: isNew
          ? "New Journal was added."
          : "Changes saved successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      if (isNew && result._id) {
        navigate(`/journals/${result._id}`);
      }
    } catch (error) {
      console.error("❌ Error saving Journal:", error);

      toast({
        title: "Save failed",
        description: error.message || "An error occurred while saving Journal.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleClose = () => {
    navigate("/Poc");
  };

  // Helper to handle journalLine value change
  const handleJournalLineChange = (index, value) => {
    setJournalLines((prev) =>
      prev.map((line, i) => (i === index ? value : line))
    );
  };

  return (
    <Box>
      <Navbar />
      <Box position="relative" width="100%" minHeight="100vh">
        <Image
          src={HeroImage}
          alt="Hero Watermark"
          width="100%"
          height="100%"
          objectFit="cover"
          opacity={0.35}
          position="absolute"
          top={0}
          left={0}
          zIndex={-1}
          filter="blur(1px)"
        />

        <Box position="relative" zIndex={1} p={10}>
          <Box
            borderWidth="1px"
            borderRadius="md"
            p={1}
            mb={2}
            borderColor="gray.500"
            backgroundColor="whiteAlpha.800">
            <Flex mb={2}>
              <HStack spacing={6}>
                <Tooltip
                  label="Back to the Environment Homepage"
                  aria-label="Back To Environment tooltip">
                  <IconButton
                    icon={<HiOutlineArrowNarrowLeft />}
                    size="md"
                    onClick={handleClose}
                  />
                </Tooltip>
                <Heading size="lg">Edit Journal</Heading>
                <Tooltip label="Save Journal" aria-label="Save Journal Tooltip">
                  <IconButton
                    icon={<HiOutlineSave />}
                    size="md"
                    onClick={handleSave}
                    isDisabled={isReadOnly}
                  />
                </Tooltip>
              </HStack>
            </Flex>
          </Box>

          <Tabs orientation="horizontal" variant="line" colorScheme="linkedin">
            <TabList>
              <Tab>Journal Details</Tab>
              <Tab>Journal Lines</Tab>
            </TabList>
            <TabPanels>
              <TabPanel>
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={4}
                  borderColor="gray.500"
                  backgroundColor="whiteAlpha.800">
                  <UuidPreview uuid={journalHeader._id} />
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mt={4}>
                    <FormControl>
                      <FormLabel>Name</FormLabel>
                      <Input
                        value={journalHeader.name || ""}
                        onChange={(e) =>
                          handleHeaderChange("name", e.target.value)
                        }
                      />
                    </FormControl>

                    <CodesetSelect
                      label="Type"
                      codesetCode="SLC003"
                      value={
                        journalHeader.type
                          ? { code: journalHeader.type, description: "" }
                          : null
                      }
                      onChange={(val) => handleHeaderChange("type", val?.code)}
                    />
                    <FormControl>
                      <FormLabel>Transaction Reference</FormLabel>
                      <Input
                        value={journalHeader.transactionReference || ""}
                        onChange={(e) =>
                          handleHeaderChange(
                            "transactionReference",
                            e.target.value
                          )
                        }
                      />
                    </FormControl>
                  </SimpleGrid>

                  <SimpleGrid
                    columns={{ base: 1, md: 5 }}
                    spacing={6}
                    mt={4}></SimpleGrid>
                </Box>
              </TabPanel>
              <TabPanel>
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  p={4}
                  borderColor="gray.500"
                  backgroundColor="whiteAlpha.800">
                  <HStack alignItems="center" mb={4}>
                    <Heading size="md">Journal Lines</Heading>
                    <IconButton
                      icon={<HiOutlinePlus />}
                      size="md"
                      onClick={() => setJournalLines([...journalLines, ""])}
                      aria-label="Add Journal Line"
                    />
                  </HStack>
                  <Box overflowX="auto">
                    <table
                      style={{ width: "100%", minWidth: "1800px", borderCollapse: "collapse", variant: "unstyled", fontSize: "0.75rem" }}>
                      <thead>
                        <tr>
                          <th
                            style={{
                              textAlign: "left",
                              padding: "2px",
                              size: "xs",
                              minWidth: "200px",
                            }}>
                            Name
                          </th>
                          <th
                            style={{
                              textAlign: "left",
                              padding: "2px",
                              size: "xs",
                              minWidth: "225px",
                            }}>
                            Type
                          </th>
                          <th
                            style={{
                              textAlign: "left",
                              padding: "2px",
                              size: "xs",
                              minWidth: "225px",
                            }}>
                            Transaction Type
                          </th>
                          <th
                            style={{
                              textAlign: "left",
                              padding: "2px",
                              size: "xs",
                              minWidth: "75px",
                            }}>
                            Orig Ccy
                          </th>
                          <th
                            style={{
                              textAlign: "right",
                              padding: "2px",
                              size: "xs",
                              minWidth: "125px",
                            }}>
                            Orig Amt
                          </th>
                          <th
                            style={{
                              textAlign: "left",
                              padding: "2px",
                              size: "xs",
                              minWidth: "75px",
                            }}>
                            Sett Ccy
                          </th>
                          <th
                            style={{
                              textAlign: "right",
                              padding: "2px",
                              size: "xs",
                              minWidth: "125px",
                            }}>
                            Sett Amt
                          </th>
                          <th
                            style={{
                              textAlign: "right",
                              padding: "2px",
                              size: "xs",
                              minWidth: "125px",
                            }}>
                            Exchange Rate
                          </th>
                          <th
                            style={{
                              textAlign: "left",
                              padding: "2px",
                              size: "xs",
                            }}>
                            Due Date{" "}
                          </th>
                          <th
                            style={{
                              textAlign: "left",
                              padding: "2px",
                              size: "xs",
                            }}>
                            Payable Date{" "}
                          </th>
                          <th
                            style={{
                              textAlign: "left",
                              padding: "2px",
                              size: "xs",
                            }}>
                            Paid Date{" "}
                          </th>
                          <th
                            style={{
                              textAlign: "left",
                              padding: "2px",
                              size: "xs",
                            }}>
                            Value Date{" "}
                          </th>
                          <th
                            style={{
                              textAlign: "left",
                              padding: "2px",
                              size: "xs",
                              minWidth: "175px",
                            }}>
                            Payable By{" "}
                          </th>
                          <th
                            style={{
                              textAlign: "left",
                              padding: "2px",
                              size: "xs",
                              minWidth: "175px",
                            }}>
                            Payable To{" "}
                          </th>
                          <th
                            style={{
                              textAlign: "right",
                              padding: "2px",
                              size: "xs",
                              minWidth: "175px",
                            }}>
                            DR{" "}
                          </th>
                          <th
                            style={{
                              textAlign: "right",
                              padding: "2px",
                              size: "xs",
                              minWidth: "175px",
                            }}>
                            CR{" "}
                          </th>
                          <th
                            style={{
                              textAlign: "left",
                              padding: "2px",
                              size: "xs",
                              minWidth: "175px",
                            }}>
                            LinkId{" "}
                          </th>
                          <th style={{ width: "0px" }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {journalLines.map((line, index) => (
                          <tr key={index}>
                            <td style={{ padding: "2px" }}>
                              <Input
                                size="xs"
                                value={line.name || ""}
                                onChange={(e) =>
                                  handleJournalLineChange(index, {
                                    ...line,
                                    name: e.target.value,
                                  })
                                }
                                placeholder="Name"
                              />
                            </td>
                            <td style={{ padding: "2px" }}>
                              <CodesetSelect
                                size="xs"
                                codesetCode="SLC004"
                                value={
                                  line.type
                                    ? { code: line.type, description: "" }
                                    : null
                                }
                                onChange={(val) =>
                                  handleJournalLineChange(index, {
                                    ...line,
                                    type: val ? val.code : "",
                                  })
                                }
                              />
                            </td>
                            <td style={{ padding: "2px" }}>
                              <CodesetSelect
                                size="xs"
                                codesetCode="SLC013"
                                value={
                                  line.transactionType
                                    ? { code: line.transactionType, description: "" }
                                    : null
                                }
                                onChange={(val) =>
                                  handleJournalLineChange(index, {
                                    ...line,
                                    transactionType: val ? val.code : "",
                                  })
                                }
                              />
                            </td>
                            <td style={{ padding: "2px" }}>
                              <Input
                                size="xs"
                                value={line.originalCcy || ""}
                                onChange={(e) =>
                                  handleJournalLineChange(index, {
                                    ...line,
                                    originalCcy: e.target.value,
                                  })
                                }
                                placeholder="Ccy"
                              />
                            </td>
                            <td style={{ padding: "2px" }}>
                              <Input
                                size="xs"
                                type="number"
                                textAlign="right"
                                value={line.originalAmount || 0}
                                onChange={(e) =>
                                  handleJournalLineChange(index, {
                                    ...line,
                                    originalAmount:
                                      parseFloat(e.target.value) || 0,
                                  })
                                }
                                placeholder="0.00"
                              />
                            </td>
                            <td style={{ padding: "2px" }}>
                              <Input
                                size="xs"
                                value={line.settlementCcy || ""}
                                onChange={(e) =>
                                  handleJournalLineChange(index, {
                                    ...line,
                                    settlementCcy: e.target.value,
                                  })
                                }
                                placeholder="Ccy"
                              />
                            </td>
                            <td style={{ padding: "2px" }}>
                              <Input
                                size="xs"
                                type="number"
                                textAlign="right"
                                value={line.settlementAmount || 0}
                                onChange={(e) =>
                                  handleJournalLineChange(index, {
                                    ...line,
                                    settlementAmount:
                                      parseFloat(e.target.value) || 0,
                                  })
                                }
                                placeholder="0.00"
                              />
                            </td>
                            <td style={{ padding: "2px" }}>
                              <Input
                                size="xs"
                                type="number"
                                textAlign="right"
                                value={line.exchangeRate || 0}
                                onChange={(e) =>
                                  handleJournalLineChange(index, {
                                    ...line,
                                    exchangeRate:
                                      parseFloat(e.target.value) || 0,
                                  })
                                }
                                placeholder="0.00"
                              />
                            </td>
                            <td style={{ padding: "2px" }}>
                              <Input
                                size="xs"
                                type="date"
                                value={
                                  line.dueDate ? line.dueDate.split("T")[0] : ""
                                }
                                onChange={(e) =>
                                  handleJournalLineChange(index, {
                                    ...line,
                                    dueDate: e.target.value,
                                  })
                                }
                              />
                            </td>
                            <td style={{ padding: "2px" }}>
                              <Input
                                size="xs"
                                type="date"
                                value={
                                  line.payableByDate ? line.payableByDate.split("T")[0] : ""
                                }
                                onChange={(e) =>
                                  handleJournalLineChange(index, {
                                    ...line,
                                    payableByDate: e.target.value,
                                  })
                                }
                              />
                            </td>
                            <td style={{ padding: "2px" }}>
                              <Input
                                size="xs"
                                type="date"
                                value={
                                  line.paidDate ? line.paidDate.split("T")[0] : ""
                                }
                                onChange={(e) =>
                                  handleJournalLineChange(index, {
                                    ...line,
                                    paidDate: e.target.value,
                                  })
                                }
                              />
                            </td>
                            <td style={{ padding: "2px" }}>
                              <Input
                                size="xs"
                                type="date"
                                value={
                                  line.valueDate ? line.valueDate.split("T")[0] : ""
                                }
                                onChange={(e) =>
                                  handleJournalLineChange(index, {
                                    ...line,
                                    valueDate: e.target.value,
                                  })
                                }
                              />
                            </td>
                            <td style={{ padding: "2px" }}>
                              <PartySelectById
                                size="xs"
                                label=""
                                role=""
                                selectedPartyId={line.payableBy || ""}
                                onChange={(val) =>
                                  handleJournalLineChange(index, {
                                    ...line,
                                    payableBy: val,
                                  })
                                }
                                placeholder="Payable By"
                              />
                            </td>
                            <td style={{ padding: "2px" }}>
                              <PartySelectById
                                size="xs"
                                label=""
                                role=""
                                selectedPartyId={line.payableTo || ""}
                                onChange={(val) =>
                                  handleJournalLineChange(index, {
                                    ...line,
                                    payableTo: val,
                                  })
                                }
                                placeholder="Payable To"
                              />
                            </td>
                            <td style={{ padding: "2px" }}>
                              <Input
                                size="xs"
                                type="number"
                                textAlign="right"
                                value={line.DR || 0}
                                onChange={(e) =>
                                  handleJournalLineChange(index, {
                                    ...line,
                                    DR: parseFloat(e.target.value) || 0,
                                  })
                                }
                                placeholder="0.00"
                              />
                            </td>
                            <td style={{ padding: "2px" }}>
                              <Input
                                size="xs"
                                type="number"
                                textAlign="right"
                                value={line.CR || 0}
                                onChange={(e) =>
                                  handleJournalLineChange(index, {
                                    ...line,
                                    CR:
                                      parseFloat(e.target.value) || 0,
                                  })
                                }
                                placeholder="0.00"
                              />
                            </td>
                            <td style={{ padding: "2px" }}>
                              <Input
                                size="xs"
                                value={line.linkId || ""}
                                onChange={(e) =>
                                  handleJournalLineChange(index, {
                                    ...line,
                                    linkId: e.target.value,
                                  })
                                }
                                placeholder="Linked Id"
                              />
                            </td>
                            <td style={{ padding: "2px" }}>
                              <IconButton
                                icon={<HiOutlineTrash />}
                                size="sm"
                                onClick={() => {
                                  const newJournalLines = journalLines.filter(
                                    (_, i) => i !== index
                                  );
                                  setJournalLines(newJournalLines);
                                }}
                                aria-label="Remove Journal Line"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                </Box>
              </TabPanel>
            </TabPanels>
          </Tabs>

          <VStack spacing={6} align="stretch"></VStack>
        </Box>
      </Box>
    </Box>
  );
};

export default JournalsEditor;
